import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

interface SessionState {
  unlockedAt: number | null;
  lastActivityAt: number;
  failedAttempts: number;
  delayUntil: number | null;

  setUnlocked: () => void;
  markActivity: () => void;
  recordFailedAttempt: () => number;
  lock: () => void;
  reset: () => void;
}

// Iteration 1 fix: off-by-one. Index 0 is unused; attempts starts at 1.
// Spec §5.3: 1st=0, 2nd=1s, 3rd=5s, 4th=30s, 5th=2m, 6-10=10m each.
const PROGRESSIVE_DELAYS_MS = [
  0,       // index 0 — unused
  0,       // 1st wrong attempt
  1_000,   // 2nd
  5_000,   // 3rd
  30_000,  // 4th
  120_000, // 5th
  600_000, // 6th
  600_000, // 7th
  600_000, // 8th
  600_000, // 9th
  600_000, // 10th (wipe happens in Unlock.tsx at MAX_ATTEMPTS)
];

// SECURITY (F5): `failedAttempts` and `delayUntil` MUST survive page reload
// — otherwise an attacker with physical access can bypass the 10-attempt
// wipe and the progressive lockout by reloading the tab between guesses.
// Only those two fields are partialized. `unlockedAt` and `lastActivityAt`
// deliberately do NOT persist — unlock state must reset on reload so a
// stolen device cannot resume a session silently.
export const useSessionStore = create<SessionState>()(
  persist(
    (set, get) => ({
      unlockedAt: null,
      lastActivityAt: Date.now(),
      failedAttempts: 0,
      delayUntil: null,

      setUnlocked: () =>
        set({
          unlockedAt: Date.now(),
          lastActivityAt: Date.now(),
          failedAttempts: 0,
          delayUntil: null,
        }),
      markActivity: () => set({ lastActivityAt: Date.now() }),
      recordFailedAttempt: () => {
        const attempts = get().failedAttempts + 1;
        const delayIdx = Math.min(attempts, PROGRESSIVE_DELAYS_MS.length - 1);
        const delayMs = PROGRESSIVE_DELAYS_MS[delayIdx] ?? 600_000;
        set({
          failedAttempts: attempts,
          delayUntil: delayMs > 0 ? Date.now() + delayMs : null,
        });
        return delayMs;
      },
      // Deliberately does NOT reset failedAttempts or delayUntil.
      // Security: if a user has 5 wrong-PIN attempts pending a 2-minute
      // cooldown and then hits the manual "Lock" button, we don't want
      // the cooldown to silently reset. Only a successful unlock
      // (setUnlocked) or a full wipe (reset) clears those counters.
      lock: () => set({ unlockedAt: null }),
      reset: () =>
        set({
          unlockedAt: null,
          lastActivityAt: Date.now(),
          failedAttempts: 0,
          delayUntil: null,
        }),
    }),
    {
      name: 'gonka.session',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        failedAttempts: state.failedAttempts,
        delayUntil: state.delayUntil,
      }),
    },
  ),
);
